import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { ProjectView, SaveLineRequest, TimesheetSummary, WeekView } from '../types'

const BASE = '/v1/time'

export function useProjects() {
  return useQuery({
    queryKey: ['timesheet', 'projects'],
    queryFn: async () => (await apiClient.get<ProjectView[]>(`${BASE}/projects`)).data,
    // The project list changes when a project opens or closes, not while someone types.
    staleTime: 5 * 60_000,
  })
}

export function useWeek(date: string) {
  return useQuery({
    queryKey: ['timesheet', 'week', date],
    queryFn: async () => (await apiClient.get<WeekView>(`${BASE}/timesheets/week`, { params: { date } })).data,
  })
}

export function useHistory() {
  return useQuery({
    queryKey: ['timesheet', 'history'],
    queryFn: async () => (await apiClient.get<TimesheetSummary[]>(`${BASE}/timesheets/my`)).data,
  })
}

export function useTimesheetMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['timesheet'] })

  return {
    saveWeek: useMutation({
      mutationFn: async ({ weekOf, lines }: { weekOf: string; lines: SaveLineRequest[] }) =>
        (await apiClient.put<WeekView>(`${BASE}/timesheets/week`, { weekOf, lines })).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<WeekView>(`${BASE}/timesheets/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 7 part 5: projects and the company-wide approval view ---

export interface PendingSheetView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  weekStart: string
  weekEnd: string
  totalHours: number
  status: string
  submittedAt: string | null
}

export function usePendingTimesheets() {
  return useQuery({
    queryKey: ['timesheet', 'pending'],
    queryFn: async () =>
      (await apiClient.get<PendingSheetView[]>('/v1/time/timesheets/pending')).data,
  })
}

export function useProjectMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['timesheet'] })

  return {
    createProject: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ProjectView>('/v1/time/projects', body)).data,
      onSuccess: invalidate,
    }),
    updateProject: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<ProjectView>(`/v1/time/projects/${id}`, body)).data,
      onSuccess: invalidate,
    }),
  }
}
